import { type ComponentProps, forwardRef, useCallback, useMemo } from 'react';
import { type Avatar as HeroAvatar, extendVariants, AvatarIcon, useAvatar } from '@heroui/react';
import { twMerge } from 'tailwind-merge';

const bgColorOptions = [
    'bg-green-400',
    'bg-coral-400',
    'bg-purple-300',
    'bg-yellow-400',
    'bg-turquoise-400',
    'bg-blue-300',
];

const MyAvatar = forwardRef<HTMLDivElement, ComponentProps<typeof HeroAvatar>>((props, ref) => {
    const getBgColor = useCallback((index: number) => bgColorOptions[index % bgColorOptions.length], []);

    const {
        src,
        icon = <AvatarIcon />,
        alt,
        classNames,
        slots,
        name,
        showFallback,
        fallback: fallbackComponent,
        getInitials,
        getAvatarProps,
        getImageProps,
    } = useAvatar({
        ref,
        // By default, getInitials grabs the first three letters of the first name. We're modifying
        // that functionality to instead grab the first letter of both the first and last name.
        getInitials: (text: string) => {
            const words = text.split(' ');
            if (words.length === 0) return '';
            if (words.length === 1) return words[0][0] || '';
            return (words[0][0] || '') + (words[words.length - 1][0] || '');
        },
        ...props,
    });

    const fallback = useMemo(() => {
        if (!showFallback && src) return null;

        const ariaLabel = alt || name || 'avatar';

        if (fallbackComponent) {
            return (
                <div aria-label={ariaLabel} className={slots.fallback({ class: classNames?.fallback })} role="img">
                    {fallbackComponent}
                </div>
            );
        }

        return name ? (
            <span
                aria-label={ariaLabel}
                className={twMerge(slots.name({ class: classNames?.name }), 'text-semibold text-[22px] text-white')}
                role="img"
            >
                {getInitials(name)}
            </span>
        ) : (
            <span aria-label={ariaLabel} className={slots.icon({ class: classNames?.icon })} role="img">
                {icon}
            </span>
        );
    }, [showFallback, src, alt, name, classNames, fallbackComponent, slots, getInitials, icon]);

    return (
        <div
            {...getAvatarProps()}
            className={twMerge(
                getAvatarProps().className,
                'shrink-0', // dont allow flex container to deform the avatar
                Number.isInteger(props.tabIndex) && getBgColor(props.tabIndex!), // apply background color if tabIndex is defined
            )}
        >
            {src && <img {...getImageProps()} alt={alt} />}
            {fallback}
        </div>
    );
});

export const Avatar = extendVariants(MyAvatar, {
    variants: {
        size: {
            lg: {
                base: 'w-[50px] h-[50px]',
            },
        },
    },
    defaultVariants: {
        size: 'lg',
    },
});
